import { createContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  signInAnonymously as firebaseSignInAnonymously,
  GoogleAuthProvider,
  linkWithPopup,
  signOut as firebaseSignOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updateProfile,
  type User,
} from 'firebase/auth'
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase'
import { requestAndSaveFcmToken } from '@/lib/pushNotifications'

interface AuthContextType {
  user: User | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signInAnonymously: () => Promise<void>
  sendEmailLink: (email: string) => Promise<void>
  updateDisplayName: (name: string) => Promise<void>
  linkWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | null>(null)

const googleProvider = new GoogleAuthProvider()

async function createUserProfile(user: User) {
  const userRef = doc(db, 'users', user.uid, 'profile', 'main')
  const snap = await getDoc(userRef)
  if (!snap.exists()) {
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      isAnonymous: user.isAnonymous,
      createdAt: serverTimestamp(),
    })
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let emailLinkPending = false

    // Complete email link sign-in if returning from email link
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const email = window.localStorage.getItem('emailForSignIn')
      if (email) {
        emailLinkPending = true
        signInWithEmailLink(auth, email, window.location.href)
          .then(() => {
            window.localStorage.removeItem('emailForSignIn')
            window.history.replaceState(null, '', window.location.pathname)
          })
          .catch(() => {
            // Link may be expired or already used
            emailLinkPending = false
            setLoading(false)
          })
      }
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)
      if (firebaseUser) {
        emailLinkPending = false
        try {
          await createUserProfile(firebaseUser)
        } catch {
          // Firestore might not be configured yet
        }
      }
      // Don't clear loading if we're still waiting for email link sign-in
      if (!emailLinkPending) {
        setLoading(false)
      }
    })
    return unsubscribe
  }, [])

  // Auto-request push notification permission once after non-anonymous sign-in.
  // If already granted, refresh the FCM token (ensures it's tied to the current SW).
  const hasPromptedRef = useRef(false)
  useEffect(() => {
    if (!user || user.isAnonymous) return

    // If permission already granted, refresh token on every session
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      requestAndSaveFcmToken(user.uid).catch(() => {})
      return
    }

    // Otherwise, prompt once per user
    if (hasPromptedRef.current) return
    const key = `push-prompted:${user.uid}`
    if (localStorage.getItem(key)) return

    hasPromptedRef.current = true
    localStorage.setItem(key, '1')

    requestAndSaveFcmToken(user.uid).catch(() => {
      // Browser may not support notifications
    })
  }, [user])

  const signInWithGoogle = useCallback(async () => {
    await signInWithPopup(auth, googleProvider)
  }, [])

  const signInAnonymously = useCallback(async () => {
    await firebaseSignInAnonymously(auth)
  }, [])

  const sendEmailLink = useCallback(async (email: string) => {
    const actionCodeSettings = {
      url: window.location.origin,
      handleCodeInApp: true,
    }
    await sendSignInLinkToEmail(auth, email, actionCodeSettings)
    window.localStorage.setItem('emailForSignIn', email)
  }, [])

  const updateDisplayName = useCallback(async (name: string) => {
    if (!auth.currentUser) return
    await updateProfile(auth.currentUser, { displayName: name })
    // Trigger a re-render with the updated user
    setUser({ ...auth.currentUser } as User)
  }, [])

  const linkWithGoogle = useCallback(async () => {
    if (!auth.currentUser) return
    try {
      await linkWithPopup(auth.currentUser, googleProvider)
      await createUserProfile(auth.currentUser)
      // onAuthStateChanged doesn't fire reliably after linkWithPopup,
      // so explicitly update user state here.
      setUser({ ...auth.currentUser } as User)
    } catch (error: unknown) {
      const firebaseError = error as { code?: string }
      if (firebaseError.code === 'auth/credential-already-in-use') {
        throw new Error('This Google account is already linked to another account.')
      }
      throw error
    }
  }, [])

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth)
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, loading, signInWithGoogle, signInAnonymously, sendEmailLink, updateDisplayName, linkWithGoogle, signOut }}
    >
      {children}
    </AuthContext.Provider>
  )
}
